import React from 'react';
/**
 * @component ProgressBar
 *
 * @description A reusable progress bar component for video playback.
 * Displays the current progress of media playback and allows seeking by clicking.
 *
 * @example
 * // Basic usage
 * <ProgressBar
 *   ref={progressBarRef}
 *   duration={videoDuration}
 *   currentTime={videoCurrentTime}
 *   onSeek={handleSeek}
 * />
 *
 * // With custom styling
 * <ProgressBar
 *   ref={progressBarRef}
 *   duration={videoDuration}
 *   currentTime={videoCurrentTime}
 *   onSeek={handleSeek}
 *   height="10px"
 *   borderRadius="large"
 *   backgroundColor="#f0f0f0"
 *   progressColor="brand"
 * />
 */

import { View } from '@smarterservices/smarter-ui';
import { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';

const ProgressBar = forwardRef(
  (
    {
      // Required props
      duration = 0,
      currentTime = 0,
      onSeek,

      // Optional props with defaults
      isLive = false,
      dragging = false,
      scrubTime = 0,
      height = '8px',
      borderRadius = 'medium',
      backgroundColor = '#E0E0E0',
      progressColor = 'brand',
      liveColor = 'success',
      ...otherProps
    },
    ref
  ) => {
    const [progressWidth, setProgressWidth] = useState(0);

    useEffect(() => {
      if (isLive) {
        setProgressWidth('100%');
      } else {
        if (currentTime > 0 && duration > 0) {
          setProgressWidth(
            `${(dragging ? scrubTime / duration : currentTime / duration) * 100}%`
          );
        } else {
          setProgressWidth('0%');
        }
      }
    }, [isLive, currentTime, duration, dragging, scrubTime]);

    const handleClickNav = (e) => {
      if (!ref?.current || !onSeek) return;

      const rect = ref.current.getBoundingClientRect();
      let relativeX = e.clientX - rect.left;
      relativeX = Math.max(0, Math.min(relativeX, rect.width));
      const newTime = (relativeX / rect.width) * duration;
      console.log(newTime);

      onSeek(newTime);
    };
    return (
      <View
        as="div"
        position="relative"
        withVisualDebug={false}
        width="100%"
        borderRadius={borderRadius}
        background="primary"
        height={height}
        cursor="pointer"
        onClick={handleClickNav}
        themeOverride={{
          backgroundPrimary: backgroundColor,
        }}
        ref={ref}
        {...otherProps}
      >
        <View
          as="div"
          position="absolute"
          insetInlineStart="0"
          insetBlockStart="0"
          width={progressWidth}
          height={height}
          background={progressColor}
          borderRadius={borderRadius}
          zIndex="9999"
          style={{
            transition: dragging ? 'none' : 'width 0.1s ease-out',
          }}
          data-testid="progress-indicator"
        />
      </View>
    );
  }
);

/**
 * PropTypes for the ProgressBar component
 */
ProgressBar.propTypes = {
  /**
   * The total duration of the media in seconds
   */
  duration: PropTypes.number,

  /**
   * The current playback position in seconds
   */
  currentTime: PropTypes.number,

  /**
   * Callback function called when the user seeks to a new position
   * @param {number} time - The new time position in seconds
   */
  onSeek: PropTypes.func.isRequired,

  /**
   * Whether the media is being played live
   */
  isLive: PropTypes.bool,

  /**
   * Whether the user is currently dragging the progress bar
   */
  dragging: PropTypes.bool,

  /**
   * The time position when scrubbing/dragging in seconds
   */
  scrubTime: PropTypes.number,

  /**
   * The height of the progress bar
   */
  height: PropTypes.string,

  /**
   * The border radius of the progress bar
   * Can be 'small', 'medium', 'large', or a specific pixel value
   */
  borderRadius: PropTypes.string,

  /**
   * The background color of the progress bar track
   */
  backgroundColor: PropTypes.string,

  /**
   * The color of the progress indicator
   * Can be a theme color like 'brand', 'primary', or a hex value
   */
  progressColor: PropTypes.string,

  /**
   * The color used when in live mode
   * Can be a theme color like 'success', 'error', or a hex value
   */
  liveColor: PropTypes.string,
};

export default ProgressBar;
